import { CommonModule, DatePipe } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BackofficeSessionService } from '../core/backoffice-session.service';
import { BackofficeScoreReportsApiService } from '../core/backoffice-score-reports-api.service';
import { BackofficeScoreReportPayload } from '../core/backoffice-score-reports-api.types';
import { BackofficeTournamentItem } from '../core/backoffice-tournaments-api.types';
import { BackofficeTournamentsApiService } from '../core/backoffice-tournaments-api.service';
import { I18nService } from '../core/i18n.service';

@Component({
  selector: 'app-backoffice-score-reports-list-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, DatePipe],
  template: `
    <section class="page">
      <h1>{{ t('bo.reports.title') }}</h1>

      <form (ngSubmit)="openTournament()" #form="ngForm" class="toolbar card">
        <label>{{ t('bo.reports.tournament') }}
          <select name="tournamentId" required [(ngModel)]="selectedTournamentId">
            <option [ngValue]="null">{{ t('bo.reports.selectTournament') }}</option>
            @for (tournament of tournaments(); track tournament.id) {
              <option [ngValue]="tournament.id">{{ tournament.name }} ({{ tournament.slug }})</option>
            }
          </select>
        </label>
        <button type="submit" [disabled]="!form.valid || tournamentsLoading()">{{ t('bo.reports.load') }}</button>
        <a routerLink="/backoffice/session">{{ t('bo.reports.session') }}</a>
      </form>
      @if (tournamentsLoading()) {
        <p class="state">{{ t('bo.reports.tournamentsLoading') }}</p>
      }

      @if (loading()) {
        <p class="state">{{ t('bo.reports.loading') }}</p>
      } @else if (errorMessage()) {
        <p class="state state--error">{{ errorMessage() }}</p>
      } @else if (reports().length === 0 && hasLoaded()) {
        <article class="empty">
          <p><strong>{{ t('bo.reports.emptyTitle') }}</strong></p>
          <p class="state">{{ t('bo.reports.emptySubtitle') }}</p>
        </article>
      } @else if (reports().length > 0) {
        <ul class="list">
          @for (report of reports(); track report.id) {
            <li class="item">
              <p>
                <strong>{{ t('bo.reports.report', { id: report.id }) }}</strong>
                <span class="status">{{ formatReportStatus(report.status) }}</span>
                @if (report.contestation.contested) {
                  <span class="status status--contested">{{ t('bo.reports.contested') }}</span>
                }
              </p>
              <p>{{ t('bo.reports.match', { match: report.match?.id || report.bracket_match_id, team: report.reporting_team_id }) }}</p>
              <p>{{ t('bo.reports.score', { s1: report.score_team1, s2: report.score_team2, date: (report.reported_at | date: 'short') ?? '' }) }}</p>
              <p>
                <a [routerLink]="['/backoffice/tournaments', activeTournamentId(), 'score-reports', report.id]">{{ t('bo.reports.detail') }}</a>
              </p>
            </li>
          }
        </ul>
      }
    </section>
  `,
  styles: `
    .page { padding: 1.2rem; max-width: 960px; margin: 0 auto; }
    h1 { margin: 0 0 0.8rem; font-size: 1.45rem; color: var(--text-strong); }
    .toolbar { display: flex; gap: 0.6rem; align-items: end; margin-bottom: 0.8rem; flex-wrap: wrap; }
    .card { border: 1px solid var(--line-soft); border-radius: 10px; background: linear-gradient(180deg, rgba(23, 36, 58, 0.94) 0%, rgba(18, 29, 47, 0.94) 100%); padding: 0.8rem; box-shadow: var(--shadow-soft); }
    label { display: grid; gap: 0.35rem; font-size: 0.92rem; color: #dbe9ff; }
    select { padding: 0.55rem; border: 1px solid var(--line-soft); border-radius: 8px; min-width: 260px; background: rgba(13, 21, 34, 0.9); color: var(--text-strong); }
    button { border: 1px solid rgba(138, 184, 255, 0.6); border-radius: 8px; padding: 0.55rem 0.8rem; background: linear-gradient(180deg, var(--accent) 0%, var(--accent-strong) 100%); color: #091325; font-weight: 700; }
    .list { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.65rem; }
    .empty { border: 1px solid var(--line-soft); border-radius: 10px; background: linear-gradient(180deg, rgba(23, 36, 58, 0.94) 0%, rgba(18, 29, 47, 0.94) 100%); padding: 0.75rem; box-shadow: var(--shadow-soft); }
    .item { border: 1px solid var(--line-soft); border-radius: 10px; padding: 0.75rem; background: linear-gradient(180deg, rgba(23, 36, 58, 0.94) 0%, rgba(18, 29, 47, 0.94) 100%); box-shadow: var(--shadow-soft); }
    .item p { margin: 0.2rem 0; color: var(--text-muted); }
    .item strong, .empty strong { color: var(--text-strong); }
    .status { margin-left: 0.45rem; display: inline-block; font-size: 0.78rem; color: #dbe9ff; background: rgba(101, 145, 216, 0.24); border: 1px solid rgba(112, 164, 255, 0.44); border-radius: 999px; padding: 0.16rem 0.45rem; vertical-align: middle; }
    .status--contested { color: #ffe7cf; background: rgba(212, 131, 40, 0.2); border-color: rgba(236, 165, 69, 0.46); }
    .state { color: var(--text-muted); }
    .state--error { color: var(--danger); }
    a { color: var(--accent); text-decoration: none; font-weight: 600; }
  `
})
export class BackofficeScoreReportsListPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly backofficeSession = inject(BackofficeSessionService);
  private readonly api = inject(BackofficeScoreReportsApiService);
  private readonly tournamentsApi = inject(BackofficeTournamentsApiService);
  private readonly i18n = inject(I18nService);
  private readonly destroyRef = inject(DestroyRef);

  protected selectedTournamentId: number | null = null;
  protected readonly activeTournamentId = signal<number | null>(null);
  protected readonly tournaments = signal<BackofficeTournamentItem[]>([]);
  protected readonly tournamentsLoading = signal(false);
  protected readonly reports = signal<BackofficeScoreReportPayload[]>([]);
  protected readonly loading = signal(false);
  protected readonly hasLoaded = signal(false);
  protected readonly errorMessage = signal<string | null>(null);

  constructor() {
    this.loadTournaments();
    const fromRoute = Number(this.route.snapshot.paramMap.get('tournamentId'));
    if (fromRoute > 0) {
      this.selectedTournamentId = fromRoute;
      this.activeTournamentId.set(fromRoute);
      this.fetch(fromRoute);
    }
  }

  protected openTournament(): void {
    if (!this.selectedTournamentId || this.selectedTournamentId <= 0) {
      return;
    }
    void this.router.navigateByUrl(`/backoffice/tournaments/${this.selectedTournamentId}/score-reports`);
  }

  private loadTournaments(): void {
    this.tournamentsLoading.set(true);
    this.tournamentsApi
      .list()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ tournaments }) => {
          const sorted = [...tournaments].sort((a, b) => a.name.localeCompare(b.name));
          this.tournaments.set(sorted);
          if (!this.selectedTournamentId && sorted.length > 0) {
            this.selectedTournamentId = sorted[0].id;
          }
          this.tournamentsLoading.set(false);
        },
        error: (error) => {
          if (error?.status === 401) {
            this.handleUnauthorized();
            return;
          }
          if (error?.status === 403) {
            this.errorMessage.set(this.t('bo.reports.forbiddenTournaments'));
            this.tournamentsLoading.set(false);
            return;
          }
          this.tournaments.set([]);
          this.errorMessage.set(this.t('bo.reports.loadTournamentsError'));
          this.tournamentsLoading.set(false);
        }
      });
  }

  private fetch(tournamentId: number): void {
    this.loading.set(true);
    this.errorMessage.set(null);
    this.hasLoaded.set(false);
    this.api
      .list(tournamentId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ score_reports }) => {
          this.reports.set(score_reports);
          this.loading.set(false);
          this.hasLoaded.set(true);
        },
        error: (error) => {
          if (error?.status === 401) {
            this.handleUnauthorized();
            return;
          }
          if (error?.status === 403) {
            this.errorMessage.set(this.t('bo.reports.forbiddenList'));
            this.loading.set(false);
            this.hasLoaded.set(true);
            return;
          }
          const apiError = error?.error?.error;
          this.errorMessage.set(typeof apiError === 'string' ? apiError : this.t('bo.reports.loadError'));
          this.loading.set(false);
          this.hasLoaded.set(true);
        }
      });
  }

  private handleUnauthorized(): void {
    this.backofficeSession.clearToken();
    void this.router.navigateByUrl('/backoffice/session');
  }

  protected formatReportStatus(status: string): string {
    const labels: Record<string, string> = {
      reported: this.t('bo.reports.status.reported'),
      in_review: this.t('bo.reports.status.review'),
      validated: this.t('bo.reports.status.validated'),
      rejected: this.t('bo.reports.status.rejected')
    };
    return labels[status] ?? status;
  }
  protected t(key: string, params?: Record<string, string | number | null | undefined>): string {
    return this.i18n.t(key, params);
  }
}
